import React from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton, EmptyState } from '@/nova/components';
import {
  displayMetric,
  formatMoneyValue,
  metricValue,
  rowEntityName,
  type Dimension,
} from '../../reports-helpers';
import type { AffiliateCommissionsReport, AffiliateMetrics, AffiliatePerformanceRow } from '../../types';
import { Table, Th, Td, TotalRow, NameCell, Scroller } from './ReportsTab.styles';
import { Panel, TableHead, PillGroup, Pill } from './dashboard.styles';

export interface BreakdownTableProps {
  rows: AffiliatePerformanceRow[];
  dimension: Dimension;
  totals?: AffiliateMetrics;
  commissions?: AffiliateCommissionsReport;
  currency: string;
  loading: boolean;
  onDimension: (dimension: Dimension) => void;
}

const money = (n: number | undefined, currency: string): string =>
  typeof n === 'number' && Number.isFinite(n) ? formatMoneyValue(n, currency) : '–';

export const BreakdownTable = ({
  rows,
  dimension,
  totals,
  commissions,
  currency,
  loading,
  onDimension,
}: BreakdownTableProps): React.ReactElement => {
  const { t } = useTranslation();
  const unknown = t('Unknown');
  const showCommission = dimension === 'affiliate';

  const dims: Array<{ id: Dimension; label: string }> = [
    { id: 'affiliate', label: t('Affiliate') },
    { id: 'campaign', label: t('Campaign') },
    { id: 'date', label: t('Date') },
  ];

  const commissionById = React.useMemo(() => {
    const map = new Map<string, number | undefined>();
    for (const row of commissions?.affiliates ?? []) {
      const id = row.affiliate?.id;
      if (id) map.set(id, row.commission_usd);
    }
    return map;
  }, [commissions]);

  const nameFor = (row: AffiliatePerformanceRow): string =>
    dimension === 'date' ? row.date ?? unknown : rowEntityName(row, dimension, unknown);

  return (
    <Panel>
      <TableHead>
        <PillGroup role="group" aria-label={t('Breakdown dimension')}>
          {dims.map((d) => (
            <Pill key={d.id} type="button" $active={dimension === d.id} onClick={() => onDimension(d.id)}>
              {d.label}
            </Pill>
          ))}
        </PillGroup>
      </TableHead>

      {loading && rows.length === 0 ? (
        <Skeleton height={180} block />
      ) : rows.length === 0 ? (
        <EmptyState text={t('No data for this period yet')} />
      ) : (
        <Scroller>
          <Table>
            <thead>
              <tr>
                <Th>{dimension === 'date' ? t('Date') : dimension === 'campaign' ? t('Campaign') : t('Affiliate')}</Th>
                <Th $numeric>{t('Regs')}</Th>
                <Th $numeric>{t('FTDs')}</Th>
                <Th $numeric>{t('Deposits')}</Th>
                <Th $numeric>{t('NGR')}</Th>
                {showCommission ? <Th $numeric>{t('Commission')}</Th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const m = row.metrics;
                const affiliateId = row.affiliate?.id;
                return (
                  <tr key={affiliateId ?? row.date ?? row.campaign ?? `row-${index}`}>
                    <Td>
                      <NameCell text={nameFor(row)} tag="span" />
                    </Td>
                    <Td $numeric>{displayMetric(metricValue(m, 'registrations'), 'registrations', currency)}</Td>
                    <Td $numeric>{displayMetric(metricValue(m, 'ftds'), 'ftds', currency)}</Td>
                    <Td $numeric>{money(m?.deposits_usd, currency)}</Td>
                    <Td $numeric>{money(m?.ngr_usd, currency)}</Td>
                    {showCommission ? (
                      <Td $numeric>{affiliateId ? money(commissionById.get(affiliateId), currency) : '–'}</Td>
                    ) : null}
                  </tr>
                );
              })}
              {totals ? (
                <TotalRow>
                  <Td $strong>{t('Total')}</Td>
                  <Td $numeric $strong>{displayMetric(metricValue(totals, 'registrations'), 'registrations', currency)}</Td>
                  <Td $numeric $strong>{displayMetric(metricValue(totals, 'ftds'), 'ftds', currency)}</Td>
                  <Td $numeric $strong>{money(totals.deposits_usd, currency)}</Td>
                  <Td $numeric $strong>{money(totals.ngr_usd, currency)}</Td>
                  {showCommission ? (
                    <Td $numeric $strong>{money(commissions?.totals?.commission_usd, currency)}</Td>
                  ) : null}
                </TotalRow>
              ) : null}
            </tbody>
          </Table>
        </Scroller>
      )}
    </Panel>
  );
};

export default BreakdownTable;
